import callApi from '../../util/apiCaller';
import { unique, flat } from '../../util/array';
import { setAnchor } from '../../util/timeAnchor';
import { isOfflineAnswersEnabled, queueAnswer, acknowledgeAnswer, markAnswersFailed } from './OfflineAnswerActions';

import { displayTextWithFormula } from '../Subject/VariableActions';
import { getApplicantSessionId, getApplicantSelectedVariables } from '../Applicant/ApplicantActions';
import { getContentToFill, getFillSpaces, getFillSpaceChoices, getQuestionOption, QUESTION_ASSOCIATION_SIDES } from '../Question/QuestionActions';
import { getSession } from '../Session/SessionActions';
import { formatPrice } from '../../util/price';

// Export Constants
export const SET_ANSWERS = 'SET_ANSWERS';
export const SET_APPLICANT_ANSWERS = 'SET_APPLICANT_ANSWERS';

export function getSessionAnswersRequest(sessionId) {
    return dispatch => {
        return callApi(`session/${sessionId}/answers`).then(result => {
            if(result.answers) {
                const applicantsIds = unique(result.answers.map(answers => answers.applicant));
                applicantsIds.forEach(applicantId => {
                    applicantId && dispatch(setApplicantAnswers(applicantId, result.answers.filter(answer => answer.applicant === applicantId)));
                });
            }
            return result.answers;
        }).catch(err => {
            return null;
        });
    };
}

export function getApplicantAnswersRequest(applicantId, isMyAnswer = true, isTemp = false) {
    return dispatch => {
        if(applicantId) {
            return callApi(`applicant/${applicantId}/answers${isTemp ? '/temporary' : ''}`).then(result => {
                isMyAnswer ? dispatch(setAnswers(result.answers)) : dispatch(setApplicantAnswers(applicantId, result.answers));
                return result.answers;
            }).catch(err => {
                return null;
            });
        }
        return null;
    };
}

export function editAnswerRequest(answer, isMultiple = false, isMyAnswer = true) {
    return dispatch => {
        const answersToSave = (isMultiple ? (answer || []) : [answer]).filter(answerToSave => answerToSave);
        const applicantId = (answersToSave.find(answerToSave => answerToSave.applicant) || {}).applicant;

        // Write-ahead: the answers are secured on the device before being sent, then dropped as
        // soon as the server acknowledges them. Only enabled while passing an exam.
        return queueAnswers(answersToSave).then(queuedAnswers => {
            const body = isMultiple ? { answers: queuedAnswers } : { answer: queuedAnswers[0] };
            return callApi('answer/edit', 'post', body).then(result => {
                // dispatch(getApplicantAnswersRequest(applicantId, isMyAnswer));
                const successedAnswers = result?.success || [];
                setAnchor(result?.serverTime);
                isMyAnswer ? dispatch(setAnswers(successedAnswers)) : dispatch(setApplicantAnswers(applicantId, successedAnswers));
                // Awaited: the caller reads the queue right after, and an acknowledgement still in
                // flight would make answers that did reach the service look pending
                return acknowledgeAnswers(applicantId, queuedAnswers, successedAnswers, result?.errors).then(() => result);
            }).catch(err => {
                // The request never made it: what was written before sending is now the only copy
                return reportFailedAnswers(applicantId, queuedAnswers).then(() => ({ errors: [{ error: err.message }] }));
            });
        });
    };
}

/*
 * Secure the answers locally before sending them
 */
function queueAnswers(answersToSave) {
    if(!isOfflineAnswersEnabled()) {
        return Promise.resolve(answersToSave);
    }
    return Promise.all(answersToSave.map(answerToSave => queueAnswer(answerToSave)));
}

/*
 * Drop the local copy of every acknowledged answer, and of every answer the server will never
 * accept again (copy already consolidated), which would otherwise be replayed forever
 */
function acknowledgeAnswers(applicantId, queuedAnswers, successedAnswers, errors = []) {
    if(!isOfflineAnswersEnabled()) {
        return Promise.resolve();
    }
    const getQueuedSequence = questionId => ((queuedAnswers.find(queued => `${queued.question}` === `${questionId}`) || {}).offline || {}).sequence;

    const acknowledgedQuestionsIds = [
        ...(successedAnswers || []).map(savedAnswer => savedAnswer.question),
        // The service will never accept these again (copy already consolidated): replaying them
        // forever would keep the applicant blocked on a validation that can no longer succeed
        ...(errors || []).filter(error => error && error.error === 'AnswerOfflineTooLate' && error.answer).map(error => error.answer.question),
    ];

    return Promise.all(acknowledgedQuestionsIds.map(questionId => acknowledgeAnswer(applicantId, questionId, getQueuedSequence(questionId))))
        .then(() => reportFailedAnswers(applicantId, queuedAnswers.filter(queued => !acknowledgedQuestionsIds.some(questionId => `${questionId}` === `${queued.question}`))));
}

/*
 * Answers the service did not take: they stay on the device and the applicant is told
 */
function reportFailedAnswers(applicantId, failedAnswers) {
    if(!isOfflineAnswersEnabled()) {
        return Promise.resolve();
    }
    return markAnswersFailed(applicantId, (failedAnswers || []).map(failedAnswer => failedAnswer.question));
}

export function recoverApplicantTempAnswersRequest(applicantId) {
    return dispatch => {
        if(applicantId) {
            return callApi(`applicant/${applicantId}/answers/recover`).then(result => {
                dispatch(setApplicantAnswers(applicantId, result.answers));
                return result.answers;
            }).catch(err => {
                return null;
            });
        }
        return null;
    };
}
export function clearApplicantTempAnswersRequest(applicantId) {
    return dispatch => {
        if(applicantId) {
            return callApi(`applicant/${applicantId}/answers/clear`, 'delete').then(result => {
                result.ok && dispatch(setApplicantAnswers(applicantId, []));
                return result.ok;
            }).catch(err => {
                return null;
            });
        }
        return null;
    };
}

// Getters
export function getApplicantAnswers(store, applicantId, sessionId = null) {
    if(sessionId) {
        const session = getSession(store, sessionId);
        if(session && session.applicants) {
            const applicant = session.applicants.find(applicant => applicant._id.toString() === applicantId.toString());
            if(applicant) {
                return applicant.answers || [];
            }
        }
    } else {
        return store.answers.data.filter(answer => answer?.applicant?.toString() === applicantId?.toString());
    }
    return [];
}

export function getApplicantAnswer(store, applicantId, questionId) {
    return getApplicantAnswers(store, applicantId).find(answer => answer.question.toString() === questionId.toString());
}

export function getApplicantQuestionAnswer(applicant, questionId) {
    if(applicant.answers) {
        return applicant.answers.find(answer => answer.question.toString() === questionId.toString()) || null;
    }
    return null;
}

export function getGroupAnswers(store, sessionId, questionId) {
    let answers = [];
    const sessionApplicantsGroup = store.applicants.groups.filter(applicant => getApplicantSessionId(applicant) === sessionId);
    sessionApplicantsGroup.forEach(applicant => {
        if(applicant.answers) {
            answers = answers.concat(applicant.answers.filter(answer => answer.question === questionId));
        }
    });
    return answers;
}

export function hasAnswerChanged(prevAnswer, newAnswer, questionType) {
    if(!newAnswer) {
        return false;
    }
    let questionTypeConditionResult = false;

    let prevAnswerValues = (prevAnswer || {}).values || [];
    let newAnswerValues = (newAnswer || {}).values || [];

    if(prevAnswer) {
        switch(questionType) {
            case 'closed':
            case 'qcm':
            case 'file':
            case 'audio':
                questionTypeConditionResult = newAnswer.reasonning !== prevAnswer.reasonning;
                break;

            case 'association':
                prevAnswerValues = prevAnswer.values && prevAnswerValues.map(association => Object.values(association).join('-'));
                newAnswerValues = newAnswer.values && newAnswerValues.map(association => Object.values(association).join('-'));
                break;

            case 'fill':
                // First item of values is always empty due to block index started to 1 (not 0)
                prevAnswerValues = (prevAnswer.values || []) && prevAnswerValues.filter((v, i) => i > 0);
                newAnswerValues = (newAnswer.values || []) && newAnswerValues.filter((v, i) => i > 0);
                break;

            default:
                break;
        }
    }

    const result = questionTypeConditionResult
        || (
            (
                !prevAnswer
                && newAnswer
            )
            || (
                prevAnswer
                && !newAnswer
            )
            || (
                newAnswerValues
                && !prevAnswerValues
            )
            || (
                !newAnswerValues
                && prevAnswerValues
            )
            || newAnswerValues.length !== prevAnswerValues.length
            || newAnswerValues.some((value, index) => prevAnswerValues[index] !== value)
        );
    // console.log('hasAnswerChange', questionType, questionTypeConditionResult, (prevAnswer || {}).values, (newAnswer || {}).values, prevAnswerValues, newAnswerValues, newAnswerValues.length !== prevAnswerValues.length, newAnswerValues && prevAnswerValues && newAnswerValues.some((value, index) => prevAnswerValues[index] !== value), 'result:', result);
    return result;
}

export function getAnswerMark(answer, applicant, question, log = false) {
    const goodAnswers = question && question.answers ? flat(question.answers.filter(answer => answer).map(goodAnswer => displayTextWithFormula(goodAnswer, getApplicantSelectedVariables(applicant)) || '')) : [];
    let markValue = 0;

    // console.log('premarkAnswer', question.type, question.label);
    // console.log('good answers', goodAnswers);
    // console.log('applicant answers', answer?.values);
    if(question && answer) {
        const questionTotalPoints = parseFloat(`${question.points || ''}`.replace(',', '.')) || 0;
        const questionTotalNegativePoints = parseFloat(`${question.negativePoints || ''}`.replace(',', '.')) || 0;
        switch(question.type) {
            case 'closed': {
                const isString = (question.options && question.options.isString) || false;
                const isCaseSensitive = (isString && (question.options && question.options.isCaseSensitive)) || false;
                let answerValue = answer.values && answer.values.length ? `${answer.values[0]}` : '';
                answerValue = isString ? answerValue.trim() : answerValue.replace(',', '.');
                answerValue = isCaseSensitive ? answerValue : answerValue.toLowerCase();

                const tolerance = (!isString && question.options && question.options.tolerance && question.options.tolerance.replace(',', '.')) || 0;
                const isTolerancePercent = !isString && tolerance && tolerance.indexOf('%') !== -1;
                // console.log('answer value', answerValue, 'isString?', isString, 'tolerance', tolerance, 'isTolerancePercent', isTolerancePercent);
                // console.log(question, goodAnswers);
                if(
                    (isString && goodAnswers.map(goodAnswer => ((isCaseSensitive ? goodAnswer : (goodAnswer || '').toLowerCase()) || '').trim()).includes(answerValue))
                    || goodAnswers.map(goodAnswer => (goodAnswer || '').replace(',', '.')).some(goodAnswer => {
                        let answerTolerance = parseFloat(tolerance);
                        if(isTolerancePercent) {
                            answerTolerance = (parseFloat(goodAnswer) * parseFloat(tolerance.replace('%', ''))) / 100;
                        }
                        // answerTolerance && console.log('answer tolerance', answerTolerance);
                        // answerTolerance && console.log('Answer must be included between:', (parseFloat(goodAnswer) - answerTolerance), ' and ', (parseFloat(goodAnswer) + answerTolerance));
                        return (parseFloat(goodAnswer) - answerTolerance) <= parseFloat(answerValue) && (parseFloat(goodAnswer) + answerTolerance) >= parseFloat(answerValue);
                    })
                ) {
                    // console.log('Good Answer');
                    markValue = questionTotalPoints;
                } else if(questionTotalNegativePoints && answerValue !== '') {
                    markValue = -questionTotalNegativePoints;
                    // console.log('Bad Answer', markValue);
                }
                break;
            }

            case 'qcm': {
                // TODO: Disable premark in case of all answers selected without negative points and with allowPartialGoodAnswer option
                const answerValues = (answer.values || []).filter(val => val);
                if(getQuestionOption(question, 'allowPartialGoodAnswer') && goodAnswers.length) {
                    const unitAnswerMark = questionTotalPoints / goodAnswers.length;
                    // console.log({ unitAnswerMark, good: answerValues.filter(answerValue => goodAnswers.includes(answerValue)), bad: answerValues.filter(answerValue => !goodAnswers.includes(answerValue)) });
                    markValue = answerValues.reduce((total, answerValue) => total + (
                        goodAnswers.includes(answerValue)
                        ? unitAnswerMark // If answer is good => Add fraction points
                        : (questionTotalNegativePoints > 0 ? (questionTotalNegativePoints * -1) : 0) // If not good & negative points exist, substract negative points
                    ), 0);
                } else if(answerValues.length && goodAnswers.every(goodAnswer => answerValues.includes(goodAnswer)) && goodAnswers.length === answerValues.length) {
                    // console.log('Good Answer');
                    markValue = questionTotalPoints;
                } else if(questionTotalNegativePoints && answerValues.length > 0) {
                    // markValue = - (answer.values || []).filter(answer => !goodAnswers.includes(answer)).reduce((total, badAnswer) => {
                    //     return total + questionTotalNegativePoints;
                    // }, 0);
                    markValue = -questionTotalNegativePoints;
                    // console.log('Bad Answer', markValue);
                }
                break;
            }

            case 'fill': {
                const answerValues = (answer.values || []);
                const fillSpaces = getFillSpaces(question);
                const applicantGoodAnswers = question.answers.filter((questionAnswer, index) => {
                    // log && console.log(questionAnswer, index, fillSpaces.includes(index), answerValues[index], questionAnswer?.includes(answerValues[index]));
                    return fillSpaces.includes(index) && questionAnswer?.includes(answerValues[index]);
                });
                const canMark = fillSpaces.every(fillIndex => getFillSpaceChoices(question, fillIndex).length) && (question.choices.length - 1) === fillSpaces.length;
                log && console.log({
                    questionChoices: question.choices,
                    applicantGoodAnswers,
                    applicantAnswers: answerValues,
                    fillSpaces,
                    canMark,
                });
                if(canMark) { // every choice is a set of array not empty (=> no free answer)
                    // goodAnswers = question.answers.filter((answer, index) => index === 0 || (answer || '').includes(answerValues[index])).slice(1);
                    // console.log(question.answers.slice(1), goodAnswers, `${goodAnswers.length} ?== ${question.answers.slice(1).length}`);
                    // console.log({
                    //     answerValues,
                    //     goodAnswers,
                    //     questionAnswers: question.answers,
                    //     questionAnswersSliced: question.answers.slice(1),
                    // });
                    if(applicantGoodAnswers.length === question.answers.filter((answer, index) => index === 0 || answer).slice(1).length) {
                        log && console.log(question.answers.filter((answer, index) => index === 0 || answer).slice(1));
                        markValue = questionTotalPoints;
                    } else if(answerValues.length > 0) {
                        log && console.log('Answers exist but wrong', getQuestionOption(question, 'allowPartialGoodAnswer') && 'allowPartialGoodAnswer');
                        if(getQuestionOption(question, 'allowPartialGoodAnswer')) {
                            // Retrait des points négatifs (si défini) ou au prorata des choix disponibles
                            const pointsByField = questionTotalPoints / fillSpaces.length;
                            markValue = applicantGoodAnswers.length * pointsByField;
                            if(questionTotalNegativePoints) {
                                // Décompte des negativePoints par réponse fausse
                                markValue -= (answerValues.filter(value => value).length - applicantGoodAnswers.length) * questionTotalNegativePoints;
                            }
                        } else if(questionTotalNegativePoints) {
                            markValue = -questionTotalNegativePoints;
                        }
                    }
                    // OU ??
                    // 1 fois negativePoints si une ou plusieurs réponses sont fausses
                }
                break;
            }

            case 'association': {
                const answerValues = (answer.values || []);
                const goodAnswersValues = question.answers.filter(goodAssociation => answerValues.find(answerAssociation => Object.keys(goodAssociation).every(side => parseInt(answerAssociation[side], 10) === parseInt(goodAssociation[side], 10))));
                // console.log('questionTotalNegativePoints', questionTotalNegativePoints);
                // console.log('choices', question.choices);
                // console.log('good answers', goodAnswers);
                // console.log('applicant answers', answerValues);
                if(question.answers.length === answerValues.length && goodAnswersValues.length === question.answers.length) {
                    markValue = questionTotalPoints;
                } else if(answerValues.length > 0) {
                        if(getQuestionOption(question, 'allowPartialGoodAnswer')) {
                        const pointsByField = questionTotalPoints / goodAnswers.length;

                        // console.log('pointsByField', pointsByField);
                        // console.log('goodAnswersValues', goodAnswersValues);
                        // console.log('Max answers checked possible', QUESTION_ASSOCIATION_SIDES.reduce((total, side) => total * (question.choices || []).filter(choice => choice.side === side).length, 1));
                        if(questionTotalNegativePoints) {
                            markValue = (goodAnswersValues.length * pointsByField) - ((goodAnswers.length - goodAnswersValues.length) * questionTotalNegativePoints);
                        } else if(answerValues.length < QUESTION_ASSOCIATION_SIDES.reduce((total, side) => total * (question.choices || []).filter(choice => choice.side === side).length, 1)) { // prevent all possibilities are checked
                            markValue = goodAnswersValues.length * pointsByField;
                        }
                    } else if(questionTotalNegativePoints) {
                        markValue = -questionTotalNegativePoints;
                    }
                }
                break;
            }

            case 'file':
            case 'audio':
            case 'open':
            default:
        }
    } else {
        !question && console.error('Missing question', answer, question);
    }

    return formatPrice(markValue);
}

// Export Actions
export function setAnswers(answers) {
    return {
        type: SET_ANSWERS,
        answers,
    };
}

export function setApplicantAnswers(applicantId, answers) {
    return {
        type: SET_APPLICANT_ANSWERS,
        applicantId,
        answers,
    };
}
